/**
 * Pure venue-flag resolution logic shared by both SSR (RedisWaffle) and
 * client-side (WaffleContext) flag checks.
 *
 * Mirrors Django FeatureFlag.is_active venue logic:
 * 1. Excluded venues → always inactive
 * 2. Included venues → always active (bypasses percent)
 * 3. Venue in venue_ids (percent-eligible) → delegate to baseFlagActive
 *    (which already contains the percent bucketing result)
 * 4. venue_ids has entries but venue isn't listed → inactive
 * 5. No venue scoping → delegate to baseFlagActive
 */

export interface VenueFlagData {
  venue_ids: number[];
  included_venue_ids: number[];
  excluded_venue_ids: number[];
}

export function resolveVenueFlag(
  flag: VenueFlagData,
  venueId: number | undefined,
  baseFlagActive: boolean
): boolean {
  const scopedVenues = flag.venue_ids ?? [];
  const includedVenues = flag.included_venue_ids ?? [];
  const excludedVenues = flag.excluded_venue_ids ?? [];

  if (venueId !== undefined && excludedVenues.includes(venueId)) return false;
  if (venueId !== undefined && includedVenues.includes(venueId)) return true;

  if (venueId !== undefined && scopedVenues.includes(venueId)) {
    return baseFlagActive;
  }

  if (scopedVenues.length > 0) return false;

  return baseFlagActive;
}

/**
 * Parse the waffle percent cookie for a flag from a cookie header string.
 * Django waffle sets cookies named `dwf_{flagName}` with value "True"/"False".
 * Returns undefined if the cookie is not present (first-time visitor).
 */
export function parseWaffleCookie(
  flagName: string,
  cookieHeader: string | undefined
): boolean | undefined {
  if (!cookieHeader) return undefined;

  const cookieName = `dwf_${flagName}`;
  const match = cookieHeader
    .split(";")
    .map((c) => c.trim())
    .find((c) => c.startsWith(`${cookieName}=`));

  if (!match) return undefined;

  return match.split("=")[1] === "True";
}
